"use client";

import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  MeshGradientBackground,
  type MeshGradientColor,
} from "@/components/assets/background/gradientBackground";

type ExpandableModalProps = {
  children: React.ReactNode;
  buttonText?: string;
  buttonIcon?: React.ReactNode;
  buttonClassName?: string;
  backgroundType?: "solid" | "mesh";
  backgroundColor?: string; // For solid backgrounds
  meshColor?: MeshGradientColor; // For mesh backgrounds
  showCloseButton?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  isOpen?: boolean; // Controlled state
  hideButton?: boolean; // Option to hide the trigger button
  fullWidth?: boolean; // Option for full width content without max-width constraint
};

export default function ExpandableModal({
  children,
  buttonText = "Open",
  buttonIcon,
  buttonClassName,
  backgroundType = "solid",
  backgroundColor = "#004FE5",
  meshColor = "primary",
  showCloseButton = true,
  onOpen,
  onClose,
  isOpen: controlledIsOpen,
  hideButton = false,
  fullWidth = false,
}: ExpandableModalProps) {
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isExpanded =
    controlledIsOpen !== undefined ? controlledIsOpen : internalIsExpanded;

  useEffect(() => {
    setMounted(true);
  }, []);

  // Global cleanup effect to ensure scroll is restored on unmount
  useEffect(
    () => () => {
      document.body.style.overflow = "unset";
    },
    []
  );

  const handleExpand = () => {
    if (controlledIsOpen === undefined) {
      setInternalIsExpanded(true);
    }
    onOpen?.();
  };

  const handleClose = () => {
    if (controlledIsOpen === undefined) {
      setInternalIsExpanded(false);
    }
    onClose?.();
  };

  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup function to ensure scroll is restored on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isExpanded]);

  const modalContent = (
    <AnimatePresence initial={false}>
      {isExpanded && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-1 sm:p-2 md:p-3">
          <motion.div
            className="relative flex h-full w-full transform-gpu overflow-y-auto will-change-transform"
            layout
            layoutId="expandable-modal"
            style={{
              borderRadius: "24px",
              ...(backgroundType === "solid" && { backgroundColor }),
            }}
            transition={{ duration: 0.3 }}
          >
            {/* Background Layer */}
            {backgroundType === "mesh" && (
              <div className="absolute inset-0 z-0">
                <MeshGradientBackground
                  className="absolute inset-0"
                  color={meshColor}
                  style={{ borderRadius: "24px" }}
                />
              </div>
            )}

            {/* Content */}
            <motion.div
              animate={{ opacity: 1 }}
              className={
                fullWidth
                  ? "relative z-10 flex h-full w-full flex-col items-center justify-center"
                  : "relative z-10 mx-auto flex h-full w-full max-w-[1100px] flex-col items-center justify-center gap-8 p-2 sm:p-4 md:p-6 lg:p-10 xl:p-16"
              }
              initial={{ opacity: 0 }}
              transition={{ delay: 0.15, duration: 0.4 }}
            >
              {children}
            </motion.div>

            {/* Close Button */}
            {showCloseButton && (
              <motion.button
                aria-label="Close"
                className="absolute top-6 right-6 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-transparent text-white transition-colors hover:bg-white/10"
                onClick={handleClose}
                type="button"
              >
                <X className="h-5 w-5" />
              </motion.button>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {!hideButton && (
        <AnimatePresence initial={false}>
          {!isExpanded && (
            <motion.div className="relative inline-block">
              <motion.div
                className="absolute inset-0 transform-gpu items-center justify-center overflow-hidden will-change-transform"
                layout
                layoutId="expandable-modal"
                style={{
                  borderRadius: "100px",
                  ...(backgroundType === "solid" && { backgroundColor }),
                }}
              >
                {backgroundType === "mesh" && (
                  <MeshGradientBackground
                    className="absolute inset-0"
                    color={meshColor}
                    style={{ borderRadius: "100px" }}
                  />
                )}
              </motion.div>
              <motion.button
                animate={{ opacity: 1, scale: 1 }}
                className={
                  buttonClassName ||
                  "relative h-15 px-6 py-3 font-regular text-[#E3E3E3] text-lg tracking-[-0.01em] sm:px-8 sm:text-xl"
                }
                exit={{ opacity: 0, scale: 0.8 }}
                initial={{ opacity: 0, scale: 0.8 }}
                layout={false}
                onClick={handleExpand}
                transition={{ delay: 0.2 }}
              >
                <span className="flex items-center gap-2">
                  {buttonIcon}
                  {buttonText}
                </span>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {mounted && createPortal(modalContent, document.body)}
    </>
  );
}
